import { Component, OnInit, inject } from '@angular/core';
import { Params } from '@angular/router';

import { take } from 'rxjs';

import { CamRoutes } from '@camelot/menu';
import { CamPermissionsService } from '@camelot/user';

import { VisitorTokenService } from 'src/app/auth/visitor-token.service';
import { quotationSignatureResultParam } from 'src/app/services/billings/quotation-signature.service';

import { EBillingsRoute } from '../../billings/billings.routes';
import { EBillingsVisitorRoute } from '../../billings/visitor-billings.routes';
import { BasePage } from '../../core/abstract/base-page';
import { EProjectsRoute } from '../../projects/projects.routes';

type Scope = '' | 'projects' | 'invoices' | 'quotations' | 'visitor-quotations' | 'domiciliation';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector

  templateUrl: './redirect.component.html',
  styleUrls: ['./redirect.component.scss'],
})
export class RedirectPage extends BasePage implements OnInit {
  private readonly _permissionsService = inject(CamPermissionsService);
  private readonly _visitorTokenService = inject(VisitorTokenService);

  private _scope: Scope = '';
  private _projectId: string = '';
  private _invoiceId: string = '';
  private _quotationId: string = '';
  private _versionId: string = '';
  private _status: string = '';
  private _signature: string = '';

  ngOnInit() {
    this._registerSubscription(
      this._getQueryParams({
        scope: '',
        projectId: '',
        invoiceId: '',
        quotationId: '',
        versionId: '',
        signature: '',
        status: '',
        token: '',
      })
        .pipe(take(1))
        .subscribe(({ scope, projectId, invoiceId, quotationId, versionId, status, token, signature }) => {
          this._scope = <Scope>scope;
          this._projectId = projectId;
          this._invoiceId = invoiceId;
          this._quotationId = quotationId;
          this._versionId = versionId;
          this._status = status;
          this._signature = signature;

          // Le lien du mail porte le token. Le retour de YouSign n'en a pas : la session visiteur
          // ouverte à l'aller est toujours là, l'écraser la rendrait inutilisable.
          if (this._scope === 'visitor-quotations' && !this._permissionsService.isAuthenticated && token) {
            this._visitorTokenService.open(token);
          }

          const urlParams = this._generateUrlParams();
          this._router.navigate([CamRoutes.getAbsoluteUrl(urlParams.enum, urlParams.data)], {
            queryParams: urlParams.queryParams,
          });
        })
    );
  }

  private _generateUrlParams(): { data: Params; enum: string[]; queryParams?: Params } {
    switch (this._scope) {
      case 'projects':
        return {
          enum: [EProjectsRoute.projects, EProjectsRoute.view, EProjectsRoute.summary],
          data: {
            id: this._projectId ?? '',
          },
        };
      case 'invoices':
        return {
          enum: [EBillingsRoute.billing, EBillingsRoute.invoices, EBillingsRoute.view],
          data: {
            id: this._invoiceId ?? '',
          },
        };
      case 'quotations':
        return {
          enum: [EBillingsRoute.billing, EBillingsRoute.quotations, EBillingsRoute.view],
          data: {
            id: this._quotationId ?? '',
            version: this._versionId ?? 'latest',
          },
        };
      case 'visitor-quotations':
        return {
          enum: [EBillingsVisitorRoute.billing, EBillingsVisitorRoute.view],
          data: {
            version: this._versionId,
          },
          queryParams: this._signature ? { [quotationSignatureResultParam]: this._signature } : undefined,
        };
      case 'domiciliation':
        return {
          enum: [EBillingsRoute.billing, EBillingsRoute.domiciliationResult],
          data: {
            status: this._status,
          },
        };
      default:
        return { enum: [], data: {} };
    }
  }
}
